import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ArrowClockwise, CheckCircle, Clock, ClockCounterClockwise} from '../../icons';
import {Spinner} from '../../Spinner';
import {ChannelLogo} from '../ChannelLogo';
import {SHOPIFY_CHANNEL} from '../channels';
import {HELP_SHOPIFY_INSTALL_LINK, LinkToArticle} from '../../help_center/LinkToArticle';
import {useShopifyInstallByLink} from '../../../context/UserModulesContext';
import {
    listShopifyLinkRequests, onShopifyLinkRequestsChanged, requestShopifyLink,
} from '../../../api/shopifyLinkRequestsApi';
import {ShopifyLinkActions} from './ShopifyLinkActions';
import {
    ACTION_INSTALL, ACTION_RENEW, STATE_EXPIRED, STATE_READY, requestRowsOf,
} from './shopifyLinkRequestsModel';

const RAW = {interpolation: {escapeValue: false}};

// El estado nunca va sólo con color (heurísticas §2): cada uno lleva su texto y un ícono de forma distinta.
// «Preparando» no es una alarma: reloj y tono neutro.
const STATE_ICONS = {
    [STATE_READY]: CheckCircle,
    [STATE_EXPIRED]: ClockCounterClockwise,
};

/**
 * Los pedidos de link de instalación de Shopify en Conexiones (tanda 23/10, `copy.md` §2): un renglón por
 * pedido abierto, con la tienda en negrita, el cliente si lo hay y el estado.
 *
 * - `preparing`: lo está preparando fede; llega por mail.
 * - `ready`: «Instalar en Shopify» y «Copiar link para tu cliente».
 * - `expired`: «Pedir un link nuevo», con el mismo pedido.
 *
 * Sólo con el flag de la agencia, y nada si no hay pedidos o si la lista no se pudo leer (el aviso es un
 * agregado de la pantalla: sin él, Conexiones es la de siempre). Se dibuja a nivel de PANTALLA y no dentro de
 * la plataforma: en la 2.1 la fila de Shopify sólo existe con una tienda conectada, y el primer pedido no
 * tendría dónde verse. Vuelve a pedir la lista cuando un modal avisa que hubo un pedido nuevo.
 *
 * `wrapper(card)` la envuelve sólo cuando hay algo que mostrar (patrón de `MercadoAdsConnectNotice`). `list`
 * y `request` son para los tests.
 */
export function ShopifyLinkRequestsNotice({wrapper = null, list = listShopifyLinkRequests, request = requestShopifyLink}) {
    const {t} = useTranslation();
    const {enabled} = useShopifyInstallByLink();
    const [requests, setRequests] = useState(null);
    const [renewing, setRenewing] = useState(null);
    const [renewError, setRenewError] = useState(null);
    // Número del último pedido de la lista: una respuesta vieja (o que llega desmontado) se descarta.
    const seq = useRef(0);

    const load = useCallback(() => {
        const current = ++seq.current;
        list().then((response) => {
            if (current !== seq.current || !response.ok) return;
            setRequests(response.requests);
        });
    }, [list]);

    useEffect(() => {
        if (!enabled) {
            seq.current += 1;
            setRequests(null);
            return undefined;
        }
        load();
        const unsubscribe = onShopifyLinkRequestsChanged(load);
        return () => {
            seq.current += 1;
            unsubscribe();
        };
    }, [enabled, load]);

    const rows = enabled ? requestRowsOf(requests) : [];
    if (!rows.length) return null;

    // El mismo POST del modal. Si sale bien, `requestShopifyLink` avisa que la lista cambió y el renglón
    // vuelve como «Estamos preparando…».
    // Sin cliente a propósito (review C-sl-fe-01): el backend encuentra el pedido vencido por la tienda y le deja
    // el cliente que tenía. Mandarlo lo haría validar contra quien pide, y la lista muestra pedidos de clientes
    // que este usuario puede no tener asignados (o ya desactivados): ese 404 dejaba el link sin renovar nunca.
    const renew = (row) => {
        setRenewing(row.id);
        setRenewError(null);
        request({shop: row.shop, agencyClientId: null}).then((response) => {
            setRenewing(null);
            if (!response.ok) setRenewError({id: row.id, key: response.errorKey});
        });
    };

    const card = (
        <section className="shl-requests" aria-labelledby="shl-requests-title" data-testid="shopify-link-requests">
            <div className="shl-requests__head">
                <ChannelLogo channel={SHOPIFY_CHANNEL} useOld={false} width="20px" height="20px"
                             hspace={0} vspace={0} marginLeft={0} padding={0}/>
                <span className="shl-requests__title" id="shl-requests-title">{t('shopify_link.requests.title')}</span>
                <LinkToArticle article={HELP_SHOPIFY_INSTALL_LINK} text={t('shopify_link.why')} plain/>
            </div>
            <ul className="shl-requests__list">
                {rows.map((row) => {
                    const Icon = STATE_ICONS[row.state] || Clock;
                    return (
                        <li key={row.id} className={`shl-row -${row.state}`} data-testid="shopify-link-request-row"
                            data-state={row.state}>
                            <Icon className="shl-row__icon" size={20} aria-hidden="true"/>
                            <div className="shl-row__main">
                                <span className="shl-row__shop">
                                    <strong>{row.shop}</strong>
                                    {row.clientName && (
                                        <span className="shl-row__client">
                                            {t('shopify_link.requests.client', {name: row.clientName, ...RAW})}
                                        </span>
                                    )}
                                </span>
                                <span className="shl-row__state" aria-live="polite">
                                    {t(row.text.key, {...row.text.args, ...RAW})}
                                </span>
                                {row.state === STATE_READY && (
                                    <span className="shl-row__hint">{t('shopify_link.requests.who_installs')}</span>
                                )}
                                {renewError && renewError.id === row.id && (
                                    <span className="shl-row__error" role="alert">{t(renewError.key)}</span>
                                )}
                            </div>
                            {row.actions.length > 0 && (
                                <div className="shl-row__actions">
                                    {row.actions.includes(ACTION_INSTALL) && <ShopifyLinkActions link={row.installLink}/>}
                                    {row.actions.includes(ACTION_RENEW) && (
                                        <button type="button" className="shl-btn" disabled={renewing === row.id}
                                                onClick={() => renew(row)} data-testid="shopify-link-renew">
                                            {renewing === row.id
                                                ? <Spinner color="positive small"/>
                                                : <ArrowClockwise size={14} aria-hidden="true"/>}
                                            {t('shopify_link.requests.renew')}
                                        </button>
                                    )}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
    return wrapper ? wrapper(card) : card;
}

export default ShopifyLinkRequestsNotice;
